import { forwardRef } from 'react';
import { Box, useTheme } from '@mui/material';
import { useEngineStrings } from '../../user_guides/i18n/useEngineStrings';
import { entryTypeAbbrKey, entryTypeLabelKey, isEntryType } from '../../utils/entryType';

/**
 * The entry's Entry Type chip (#1938, DESIGN_SYSTEM.md 8.15): S / I / T / OT
 * in the type's color, the full word on hover and for assistive technology.
 * It is the control's only trigger: clicking it opens or retracts the Entry
 * Type control, which anchors below the chip. It leads the Memo field on the
 * Ledger page, so the type reads as a note on the line (owner, 2026-09-29).
 *
 * Every chip is one width, S and OT alike, so chips line up down a table. A
 * 1px ring in the type's own color marks an entry whose type differs from the
 * payee default (a per-entry override). Until a payee is committed, an empty
 * slot of the same size holds its place (owner, 2026-09-29 dev test): the
 * field beside it never resizes as the chip comes and goes while the payee is
 * typed. The ref reaches the button, the control's anchor.
 *
 * `readOnly` (#1943) is the saved row's chip: the same look and width, but a
 * plain span that opens nothing, since a saved row changes type through Edit.
 * An untyped saved row (a line from before #1937, until the #1939 backfill)
 * shows no chip; a blank of the chip's width keeps the memo aligned.
 *
 * @param {object} props
 * @param {string} [props.type]       the entry type in force
 * @param {boolean} [props.differs]   true when it differs from the payee default
 * @param {boolean} [props.open]      the control is showing
 * @param {function} [props.onToggle] open or close the control
 * @param {boolean} [props.readOnly]  display only: a span, no toggle
 */
// One size for every chip, and for the empty slot that holds its place.
const CHIP_WIDTH = 32;
const CHIP_HEIGHT = 20;

const EntryTypeTrigger = forwardRef(({ type, differs = false, open = false, onToggle, readOnly = false }, ref) => {
  const t = useEngineStrings();
  const { ds, palette } = useTheme();
  if (!isEntryType(type) && readOnly) {
    // Untyped saved row: nothing to show, the width kept.
    return <Box aria-hidden data-testid="entry-type-blank" sx={{ flexShrink: 0, width: CHIP_WIDTH, height: CHIP_HEIGHT }} />;
  }
  if (!isEntryType(type)) {
    // The empty slot: a soft dark box, the chip's size, no letters.
    return (
      <Box
        aria-hidden
        data-testid="entry-type-slot"
        sx={{
          flexShrink: 0,
          boxSizing: 'border-box',
          width: CHIP_WIDTH,
          height: CHIP_HEIGHT,
          borderRadius: '3px',
          backgroundColor: palette.surface.page,
          boxShadow: `inset 0 0 0 1px ${palette.divider}`,
        }}
      />
    );
  }
  const word = t[entryTypeLabelKey(type)];
  const tone = palette.entryType[type];

  const look = {
    flexShrink: 0,
    boxSizing: 'border-box',
    minWidth: CHIP_WIDTH,
    height: CHIP_HEIGHT,
    padding: '3px 7px',
    fontSize: '11px',
    fontWeight: ds.font.weightMedium,
    letterSpacing: '.04em',
    lineHeight: '14px',
    textAlign: 'center',
    borderRadius: '3px',
    border: 0,
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    backgroundColor: tone.bg,
    color: tone.fg,
    // The override ring is drawn in the chip's own color, never a border,
    // so the chip keeps its size when the ring comes and goes.
    boxShadow: differs ? 'inset 0 0 0 1px currentColor' : 'none',
  };

  if (readOnly) {
    return (
      <Box
        ref={ref}
        component="span"
        title={word}
        aria-label={`${t['entryType.label']}: ${word}`}
        data-entry-type={type}
        data-testid="entry-type-chip"
        sx={{ ...look, display: 'inline-block' }}
      >
        {t[entryTypeAbbrKey(type)]}
      </Box>
    );
  }

  return (
    <Box
      ref={ref}
      component="button"
      type="button"
      onClick={onToggle}
      title={word}
      aria-label={`${t['entryType.label']}: ${word}`}
      aria-expanded={open}
      data-differs={differs ? 'true' : 'false'}
      data-entry-type={type}
      sx={{ ...look, cursor: 'pointer' }}
    >
      {t[entryTypeAbbrKey(type)]}
    </Box>
  );
});

EntryTypeTrigger.displayName = 'EntryTypeTrigger';

export default EntryTypeTrigger;
